import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, CheckCircle2, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { ContactPicker, PhoneShare } from "@/components/pots/ContactTools";
import { ItemsEditor, ShareBox } from "@/components/pots/PotItems";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

export const PotShopping = ({ p, patch }) => {
  const [people, setPeople] = useState(p.people || "");
  return (
    <section className="card-surface space-y-3 p-5" data-testid="pot-shopping">
      <h2 className="text-lg">Liste des achats</h2>
      <ItemsEditor items={p.items || []} onChange={(items) => patch({ items })} />
      <div className="flex flex-wrap items-center gap-2">
        <Input type="number" min="1" value={people} onChange={(e) => setPeople(e.target.value)} placeholder="Nombre de personnes" className="w-48" data-testid="pot-detail-people" />
        <Button variant="outline" className="rounded-full" disabled={!Number(people)} onClick={() => patch({ people: Number(people) })} data-testid="pot-detail-people-save">Répartir</Button>
      </div>
      <ShareBox total={p.items_total || 0} people={p.people} testid="pot-detail-share" />
      <p className="text-xs text-muted-foreground">La part par personne devient le montant proposé sur la page de participation.</p>
    </section>
  );
};

export const PotInvitees = ({ p, onChange }) => {
  const qc = useQueryClient();
  const [emails, setEmails] = useState("");
  const pending = p.invitees.filter((i) => !i.paid);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["pot", p.id] }); onChange?.(); };
  const { data: rel } = useQuery({ queryKey: ["relatives"], queryFn: () => api.get("/relatives").then((r) => r.data) });
  const names = Object.fromEntries((rel?.relatives || []).filter((r) => r.email).map((r) => [r.email, r.name]));
  const invite = async (list) => {
    try { const { data } = await api.post(`/pots/${p.id}/invitees`, { emails: list }); toast.success(`${data.added} invité(s) ajouté(s)`); refresh(); return true; }
    catch (e) { toast.error(formatApiError(e)); return false; }
  };
  const add = async () => { if (await invite(emails.split(/[\s,;]+/).filter(Boolean))) setEmails(""); };
  const remind = async () => {
    try {
      const { data } = await api.post(`/pots/${p.id}/remind`, { origin_url: window.location.origin });
      toast.success(data.sent ? `${data.sent} relance(s) ${data.simulated ? "simulée(s) (démo)" : "envoyée(s)"}${data.skipped ? ` · ${data.skipped} déjà relancé(s) aujourd'hui` : ""}` : "Tout le monde a déjà été relancé aujourd'hui");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (email) => { await api.delete(`/pots/${p.id}/invitees/${encodeURIComponent(email)}`); refresh(); };
  return (
    <section className="card-surface space-y-3 p-5" data-testid="pot-invitees">
      <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="text-lg">Invités ({p.invitees.length})</h2>
        <Button className="rounded-full bg-warning text-white hover:bg-warning/90" disabled={!pending.length || !p.open} onClick={remind} data-testid="pot-remind"><BellRing className="mr-1 h-4 w-4" /> Relancer {pending.length} invité{pending.length > 1 ? "s" : ""}</Button></div>
      <ContactPicker exclude={p.invitees.map((i) => i.email)} excludeId={p.relative_id} onInvite={invite} />
      <PhoneShare p={p} link={`${window.location.origin}/c/${p.token}`} excludeId={p.relative_id} />
      <div className="flex gap-2"><Textarea value={emails} onChange={(e) => setEmails(e.target.value)} rows={2} placeholder="Ou saisissez des emails, séparés par une virgule" data-testid="pot-invitee-emails" />
        <Button variant="outline" className="self-end rounded-full" disabled={!emails.trim()} onClick={add} data-testid="pot-invitee-add">Ajouter</Button></div>
      {p.invitees.length > 0 && <ul className="divide-y divide-border text-sm">{p.invitees.map((i) => (
        <li key={i.email} className="flex items-center gap-2 py-2" data-testid={`pot-invitee-${i.email}`}>
          <span className="min-w-0 flex-1 truncate">{names[i.email] ? <><b>{names[i.email]}</b> <span className="text-xs text-muted-foreground">{i.email}</span></> : i.email}</span>
          {i.paid ? <span className="badge-saving flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />A participé</span>
            : <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{i.reminders ? `relancé ${i.reminders}× · ${new Date(i.reminded_at).toLocaleDateString("fr-FR")}` : "en attente"}</span>}
          <button onClick={() => remove(i.email)} aria-label="Retirer l'invité" data-testid={`pot-invitee-remove-${i.email}`}><X className="h-3.5 w-3.5" /></button>
        </li>))}</ul>}
      <p className="text-[11px] text-muted-foreground">Un invité est marqué « A participé » quand il paie avec cette adresse email. Une seule relance par invité toutes les 20 h.</p>
    </section>
  );
};
